import { Button, Input, Select, Spinner, Text, Title } from "../uikit";
import Header from "../components/Header";
import { AnimatePresence, motion } from "motion/react";
import { routeVariants } from "../routesVariants";
import { useEffect, useState } from "react";
import Card from "../components/Card";
import Banner from "../components/Banner";

import BalanceIcon from "../img/balance.svg";
import BillIcon from "../img/bill.svg";

import Icon from "../components/Icon";
import SupportButton from "../components/SupportButton";

import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { DOMAIN } from "../consts";
import { getUserData } from "../utils/Api";

import styles from "./subscriptions.module.css";
import { getToken } from "../utils/tokenStorage";
import trackUmamiEvent from "../utils/umami";
import { snapshotBeforePaymentRedirect } from "../utils/paymentTracking";

const invoiceTypeIcons = {
  balance_topup: BalanceIcon,
  subscription: BillIcon,
};
const BALANCE_TOPUP_TARIFF_ID = "__balance_topup__";

export default function Subscription() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [paymentSystem, setPaymentSystem] = useState("");
  const { subscription } = location.state || {};
  // Подписку для продления можно задать и через ?sub=<subscription_id> (deep link из бота)
  const [searchParams] = useSearchParams();
  const subscriptionId = subscription?.id || searchParams.get("sub") || null;

  const [isInvoiceCreating, setIsInvoiceCreating] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const [tariffs, setTariffs] = useState([]);
  const [selectedTariffId, setSelectedTariffId] = useState("");
  const [selectedPackageId, setSelectedPackageId] = useState(null);

  const [paymentSystems, setPaymentSystems] = useState([]);
  const [usdToRub, setUsdToRub] = useState(78);
  const [history, setHistory] = useState([]);
  const [balance, setBalance] = useState(0);

  const [promocode, setPromocode] = useState("");
  const [topupAmount, setTopupAmount] = useState("10");
  const [currentSubscription, setCurrentSubscription] = useState(
    subscription || null
  );

  const activeSubscriptionId = currentSubscription?.id || subscriptionId;
  const displayLogin = subscription?.login || currentSubscription?.login || "";
  const privateAddresses = currentSubscription?.private_addresses || [];

  const setPaymentSystemHandler = (event) => {
    const value = event.target.value;
    setPaymentSystem(value);
  };

  const formatPaymentMethodName = (row) => {
    // Название приходит из БД (display_name_ru/display_name_en) — вместо i18n
    if (row.internal_name != "balance") {
      const dbName = i18n.language == "en" ? row.name_en : row.name_ru;
      return dbName || t(row.name, { defaultValue: row.name });
    }
    if (row.disabled && row.topup_amount != null) {
      return t("From balance (top up ${{amount}})", {
        amount: row.topup_amount,
      });
    }
    return t("From balance");
  };

  const selectedPaymentMethod = paymentSystems.find(
    (row) => row.internal_name == paymentSystem
  );

  // RUB-методы (freekassa_rub_*, platega_sbp/platega_card) списывают рубли —
  // цены показываем в рублях по курсу из /payment-methods
  const formatPrice = (usdAmount) => {
    if (selectedPaymentMethod?.currency != "RUB") {
      return "$" + usdAmount;
    }
    const rub = Math.ceil(Number(usdAmount) * usdToRub * 100) / 100;
    return `${rub} ₽`;
  };

  const normalizePrice = (price) => {
    if (typeof price == "number") {
      return price;
    }
    if (price && typeof price == "object") {
      if ("parsedValue" in price) {
        return Number(price.parsedValue);
      }
      if ("source" in price) {
        return Number(price.source);
      }
    }
    return Number(price || 0);
  };

  const getPackagePrice = (pkg = {}) => normalizePrice(pkg.price);

  const getPrivateIpsRenewalCost = (period) => {
    if (!period || !privateAddresses.length) {
      return 0;
    }
    const total = privateAddresses.reduce(
      (sum, item) =>
        sum + (Number(item.month_price || 0) * Number(period)) / 30,
      0
    );
    return Math.ceil(total * 100) / 100;
  };

  const getRenewalTotalPrice = (pkg = {}) => {
    const packagePrice = getPackagePrice(pkg);
    if (!activeSubscriptionId) {
      return packagePrice;
    }
    return (
      Math.ceil((packagePrice + getPrivateIpsRenewalCost(pkg.period)) * 100) /
      100
    );
  };

  const getPackageById = (packageId) => {
    for (const tariff of tariffs) {
      const found = (tariff.packages || []).find(
        (pkg) => pkg.package_id == packageId
      );
      if (found) {
        return found;
      }
    }
    return null;
  };

  const openTariff = (tariff) => {
    setSelectedTariffId(String(tariff.tariff_id));
    if (String(tariff.tariff_id) == BALANCE_TOPUP_TARIFF_ID) {
      setSelectedPackageId(null);
      return;
    }
    const firstPackage = (tariff.packages || [])[0] || null;
    if (firstPackage) {
      setSelectedPackageId(firstPackage.package_id);
    }
  };

  const getPlans = async (token) => {
    let url = DOMAIN + "/packages";
    if (subscriptionId) {
      url = DOMAIN + "/packages/for-subscription/" + subscriptionId;
    }
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }).then((r) => r.json());
    if (response.success) {
      const responseTariffs = response.tariffs || [];
      const topupTariff = {
        tariff_id: BALANCE_TOPUP_TARIFF_ID,
        name: t("Balance top up"),
        description: t("Top up your account balance"),
        packages: [],
      };
      const nextTariffs = subscriptionId
        ? responseTariffs
        : [...responseTariffs, topupTariff];
      setTariffs(nextTariffs);
      if (nextTariffs.length) {
        const firstTariff = nextTariffs[0];
        const firstPackage = (firstTariff.packages || [])[0] || null;
        setSelectedTariffId(String(firstTariff.tariff_id));
        if (firstPackage) {
          setSelectedPackageId(firstPackage.package_id);
        }
      }
    }
  };

  const getPaymentSystems = async (
    token,
    packageId = null,
    amount = null,
    currentPromocode = "",
    renewSubscriptionId = null
  ) => {
    const params = new URLSearchParams();
    if (packageId != null && packageId != "") {
      params.set("package_id", String(packageId));
    }
    if (amount != null && amount !== "") {
      params.set("amount", String(amount));
    }
    if (renewSubscriptionId) {
      params.set("subscription_id", String(renewSubscriptionId));
    }
    const promocodeValue = currentPromocode.trim();
    if (promocodeValue != "") {
      params.set("promocode", promocodeValue);
    }
    const query = params.toString();
    const url = DOMAIN + "/payment-methods" + (query != "" ? "?" + query : "");
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }).then((r) => r.json());
    if (response.success) {
      const rawMethods = response.currencies || [];
      setUsdToRub(Number(response.usd_to_rub) || 100);
      const methods =
        selectedTariffId == BALANCE_TOPUP_TARIFF_ID
          ? rawMethods.filter((row) => row.internal_name != "balance")
          : rawMethods;
      setPaymentSystems(methods);
      if (
        !methods.find((x) => x.internal_name == paymentSystem && !x.disabled)
      ) {
        const firstEnabled = methods.find((x) => !x.disabled);
        setPaymentSystem(firstEnabled ? firstEnabled.internal_name : "");
      }
    }
  };

  const getHistory = async (token) => {
    const response = await fetch(DOMAIN + "/invoices/history?per_page=10", {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }).then((r) => r.json());
    if (response.success) {
      setHistory(response.items || []);
    }
  };

  const getProfileBalance = async () => {
    const [success, result] = await getUserData();
    if (success) {
      setBalance(result.balance ?? 0);
    }
  };

  const createInvoice = async () => {
    try {
      setIsInvoiceCreating(true);
      const token = getToken();
      if (token == "" || token == null) {
        navigate("/login");
        return;
      }
      const isBalanceTopup = selectedTariffId == BALANCE_TOPUP_TARIFF_ID;
      if (paymentSystem == "") {
        return;
      }
      if (!isBalanceTopup && !selectedPackageId) {
        return;
      }
      const data = { currency: paymentSystem };
      if (isBalanceTopup) {
        const amount = Number(topupAmount);
        if (!Number.isFinite(amount) || amount <= 0) {
          return;
        }
        data.invoice_type = "balance_topup";
        data.amount = amount;
      } else {
        data.invoice_type = "subscription";
        data.package_id = parseInt(selectedPackageId);
        if (subscriptionId) {
          data.subscription_id = String(activeSubscriptionId || subscriptionId);
          if (displayLogin != "") {
            data.subscription_login = displayLogin;
          }
          if (subscription) {
            data.subscription = subscription;
          }
        }
        if (promocode != "") {
          data.promocode = promocode.trim();
        }
      }
      const response = await fetch(DOMAIN + "/invoices", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      }).then((r) => r.json());
      if (response.success) {
        // Любой созданный инвойс — верх воронки; факт оплаты ловится срезами
        trackUmamiEvent("Create invoice", {
          type: isBalanceTopup ? "topup" : "subscription",
          renewal: !isBalanceTopup && Boolean(subscriptionId),
          method: paymentSystem,
        });
      }
      if (response.success && response.url != null && response.url != "") {
        // Срез до ухода на платёжку (подписки или баланс): по нему ловим факт
        // оплаты ростом подписок/баланса. Продлению срез не нужен — оно не
        // меняет количество подписок, и так его не отличить от неоплаты.
        if (!subscriptionId) {
          await snapshotBeforePaymentRedirect(
            isBalanceTopup ? "topup" : "subscription",
            paymentSystem
          );
        }
        window.location.href = response.url;
        return;
      }
      if (response.success) {
        // Оплата с баланса проходит мгновенно, без платёжного URL —
        // возвращаем пользователя в аккаунт. Ивент не шлём: деньги
        // не платятся, внутренние средства.
        if (paymentSystem == "balance") {
          navigate("/account");
          return;
        }
        await init();
      }
    } finally {
      setIsInvoiceCreating(false);
    }
  };

  const init = async () => {
    setIsInitialLoading(true);
    const token = getToken();
    if (token == "" || token == null) {
      navigate("/login");
      return;
    }
    let renewSubscriptionId = subscriptionId;
    if (subscriptionId) {
      const [success, result] = await getUserData();
      if (success) {
        const freshSubscription = (result.subs || []).find(
          (row) => String(row.id) === String(subscriptionId)
        );
        if (freshSubscription) {
          setCurrentSubscription(freshSubscription);
          renewSubscriptionId = freshSubscription.id;
        }
      }
    }
    await Promise.all([
      getPlans(token),
      getPaymentSystems(
        token,
        selectedPackageId,
        null,
        promocode,
        renewSubscriptionId
      ),
      getHistory(token),
      getProfileBalance(),
    ]);
    setIsInitialLoading(false);
  };

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    const token = getToken();
    if (token == "" || token == null) {
      return;
    }
    if (selectedTariffId == BALANCE_TOPUP_TARIFF_ID) {
      getPaymentSystems(token, null, topupAmount, "");
      return;
    }
    getPaymentSystems(
      token,
      selectedPackageId,
      null,
      promocode,
      activeSubscriptionId
    );
  }, [
    selectedPackageId,
    promocode,
    selectedTariffId,
    topupAmount,
    activeSubscriptionId,
  ]);

  const selectedPackage = getPackageById(selectedPackageId);
  const isBalanceTopupSelected = selectedTariffId == BALANCE_TOPUP_TARIFF_ID;
  const topupAmountNumber = Number(topupAmount);
  const privateIpsRenewalCost = selectedPackage
    ? getPrivateIpsRenewalCost(selectedPackage.period)
    : 0;
  const renewalTotalPrice = selectedPackage
    ? getRenewalTotalPrice(selectedPackage)
    : 0;

  return (
    <AnimatePresence exitBeforeEnter>
      <motion.div
        key={isInitialLoading}
        className="container background-regular border-padding column-spacing column"
        variants={routeVariants}
        initial="initial"
        animate="final"
        exit="exit"
      >
        {isInitialLoading ? (
          <div className="center full-height">
            <Spinner />
          </div>
        ) : (
          <>
            <Header isBack={true}>
              <Title level="1" weight="3">
                {t("Subscriptions")}
              </Title>
            </Header>
            <Card>
              <div className="column card">
                <div className="row">
                  <Banner callout={t("Balance")} header={`${balance} USD`} />
                </div>
              </div>
            </Card>
            <div className="row header">
              <Text weight="2">
                {isBalanceTopupSelected
                  ? t("Balance top up")
                  : t(
                      subscriptionId
                        ? "Prolong subscription"
                        : "Buy a subscription"
                    )}{" "}
                {!isBalanceTopupSelected && subscriptionId
                  ? displayLogin || "#" + subscriptionId
                  : ""}
              </Text>
            </div>
            <Card>
              <div className="column card">
                {tariffs.map((tariff) => {
                  const isOpened = String(tariff.tariff_id) == selectedTariffId;
                  return (
                    <div
                      className={`${styles.tariffSpoiler} ${
                        isOpened ? styles.tariffSpoilerActive : ""
                      }`}
                      key={`tariff-${tariff.tariff_id}`}
                    >
                      <div
                        className={styles.tariffHeader}
                        onClick={() => openTariff(tariff)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key == "Enter" || event.key == " ") {
                            event.preventDefault();
                            openTariff(tariff);
                          }
                        }}
                      >
                        <div>
                          <div className={styles.tariffName}>
                            {tariff.name || `Tariff #${tariff.tariff_id}`}
                          </div>
                          {tariff.description ? (
                            <div className={styles.tariffDescription}>
                              {t(tariff.description, {
                                defaultValue: tariff.description,
                              })}
                            </div>
                          ) : (
                            <></>
                          )}
                        </div>
                        <div className={styles.tariffArrow}>
                          {isOpened ? "" : "+"}
                        </div>
                      </div>
                      <AnimatePresence initial={false}>
                        {isOpened && (
                          <motion.div
                            className={styles.packageCardsWrap}
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2, ease: "easeInOut" }}
                          >
                            {String(tariff.tariff_id) ==
                            BALANCE_TOPUP_TARIFF_ID ? (
                              <div className="mb-1">
                                <Input
                                  className={styles.topupAmountInput}
                                  header={t("Amount, USD")}
                                  type="number"
                                  value={topupAmount}
                                  onChange={(e) =>
                                    setTopupAmount(e.target.value)
                                  }
                                />
                              </div>
                            ) : (
                              <div className={styles.packageCards}>
                                {(tariff.packages || []).map((pkg) => {
                                  const isSelected =
                                    selectedPackageId == pkg.package_id;
                                  return (
                                    <button
                                      type="button"
                                      key={`package-${pkg.package_id}`}
                                      className={`${styles.packageCard} ${
                                        isSelected
                                          ? styles.packageCardActive
                                          : ""
                                      }`}
                                      onClick={() =>
                                        setSelectedPackageId(pkg.package_id)
                                      }
                                    >
                                      <div className={styles.packagePrice}>
                                        {formatPrice(getPackagePrice(pkg))}
                                      </div>
                                      <div className={styles.packageMeta}>
                                        {pkg.period} {t("days2")} •{" "}
                                        {pkg.connections}{" "}
                                        {t(
                                          pkg.connections == 1
                                            ? "device"
                                            : "devices2"
                                        )}
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
                <div className="mb-1">
                  <div className={`mb-05 ${styles["plans-row"]}`}>
                    <div className={styles["plans-price-label"]}>
                      {t("Price")}
                    </div>
                  </div>
                  {activeSubscriptionId &&
                  privateAddresses.length > 0 &&
                  selectedPackage ? (
                    <div
                      className={`${styles["plans-row"]} ${styles["plans-price-breakdown"]}`}
                    >
                      <div className={styles["plans-price"]}>
                        <Text>
                          {t("Subscription")}:{" "}
                          {formatPrice(getPackagePrice(selectedPackage))}
                        </Text>
                      </div>
                      <div>
                        <Text>
                          {t("Private IP addresses")}:{" "}
                          {formatPrice(privateIpsRenewalCost)}
                        </Text>
                      </div>
                      <div className={styles["plans-price"]}>
                        <Text weight="2">
                          {t("Total")}: {formatPrice(renewalTotalPrice)}
                        </Text>
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`${styles["plans-row"]} ${styles["plans-price-breakdown"]} ${styles["plans-price"]}`}
                    >
                      <div>
                        {isBalanceTopupSelected
                          ? Number.isFinite(topupAmountNumber) &&
                            topupAmountNumber > 0
                            ? formatPrice(topupAmountNumber)
                            : "-"
                          : selectedPackage
                          ? formatPrice(
                              activeSubscriptionId
                                ? renewalTotalPrice
                                : getPackagePrice(selectedPackage)
                            )
                          : "-"}
                      </div>
                    </div>
                  )}
                </div>
                <div className="mb-1">
                  <Select
                    header={t("Payment method")}
                    onChange={setPaymentSystemHandler}
                  >
                    {paymentSystems.map((row) => (
                      <option
                        key={`currency-${row.internal_name}`}
                        value={row.internal_name}
                        selected={row.internal_name === paymentSystem}
                        disabled={Boolean(row.disabled)}
                      >
                        {formatPaymentMethodName(row)}
                      </option>
                    ))}
                  </Select>
                </div>
                {!isBalanceTopupSelected && (
                  <div className="mb-1">
                    <Input
                      header={t("Promocode")}
                      onChange={(e) => setPromocode(e.target.value)}
                    />
                  </div>
                )}
                <Button
                  className="row"
                  mode="filled"
                  size="l"
                  loading={isInvoiceCreating}
                  disabled={
                    isInvoiceCreating ||
                    paymentSystem == "" ||
                    selectedPaymentMethod?.disabled ||
                    (!isBalanceTopupSelected && !selectedPackageId) ||
                    (isBalanceTopupSelected &&
                      (!Number.isFinite(topupAmountNumber) ||
                        topupAmountNumber <= 0))
                  }
                  onClick={() => createInvoice()}
                >
                  {t(
                    isBalanceTopupSelected
                      ? "Top up balance"
                      : subscriptionId
                      ? "Prolong"
                      : "Buy a subscription"
                  )}
                </Button>
              </div>
            </Card>
            <SupportButton />
            {history.length ? (
              <>
                <div className="row header">
                  <Text weight="2">{t("Invoice history")}</Text>
                </div>
                <Card>
                  <div className="column card">
                    {history
                      .sort((x, y) =>
                        x.date_created < y.date_created ? 1 : -1
                      )
                      .map((row) => {
                        return (
                          <div
                            key={`invoice-${row.date_created}-${row.invoice_type}-${row.amount}`}
                            className="row space-between vertical-center"
                          >
                            <div className="vertical-center">
                              <div className="mr-05">
                                <Icon
                                  src={
                                    invoiceTypeIcons[row.invoice_type] ||
                                    BillIcon
                                  }
                                />
                              </div>
                              <Banner
                                callout={t(row.currency, {
                                  defaultValue: row.currency,
                                })}
                                header={"$" + row.amount}
                                description={row.date_created}
                              />
                            </div>
                            <Text className={row.is_paid ? "approved" : ""}>
                              {t(row.is_paid ? "Paid" : "Wait")}
                            </Text>
                          </div>
                        );
                      })}
                  </div>
                </Card>
              </>
            ) : (
              <div></div>
            )}
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
